import type { MaybeRef } from '@vueuse/core'
import type { Swiper as SwiperClass, SwiperOptions } from 'swiper/types'
import { useEventListener } from '@vueuse/core'
import { Swiper } from 'swiper'

export function useSwiper(container: MaybeRef<HTMLElement | null>, options: MaybeRef<SwiperOptions>) {
    const swiper = shallowRef<SwiperClass | null>(null)
    const isSwiperInited = computed(() => !!swiper.value)
    const swiperActiveIndex = ref(0)
    const resolvedOptions = computed(() => unref(options))
    const isButtonPrevDisabled = ref(true)
    const isButtonNextDisabled = ref(false)

    let isDestroyed = false

    function updateIndexes(swiperInstance: SwiperClass) {
        if (isDestroyed) return

        swiperActiveIndex.value = swiperInstance.activeIndex
        isButtonPrevDisabled.value = swiperInstance.isBeginning || !swiperInstance.allowSlidePrev
        isButtonNextDisabled.value = swiperInstance.isEnd || !swiperInstance.allowSlideNext
    }

    const initSwiper = async () => {
        const el = unref(container)
        if (!el) return

        swiper.value = new Swiper(el, resolvedOptions.value)

        // Используем nextTick для предотвращения race condition
        await nextTick(() => {
            if (!swiper.value || isDestroyed) return

            // swiper.value.on('init', updateIndexes)
            swiper.value.on('slideChange', updateIndexes)

            // Начальное обновление состояния
            updateIndexes(swiper.value)
        })
    }

    const destroySwiper = () => {
        if (!swiper.value) return

        isDestroyed = true
        swiper.value.destroy(true, true)
        swiper.value = null
    }

    const reInitSwiper = async () => {
        destroySwiper()
        isDestroyed = false
        await initSwiper()
    }

    // Debounced resize handler
    const debouncedReInit = useDebounceFn(reInitSwiper, 200)

    // Lifecycle hooks
    onMounted(async () => {
        await initSwiper()
        if (window) {
            useEventListener(window, 'resize', debouncedReInit)
        }
    })

    onBeforeUnmount(() => {
        destroySwiper()
    })

    // Watchers
    watch(
        () => unref(container),
        async (val) => {
            if (val) {
               await reInitSwiper()
            }
        }
    )

    watch(
        resolvedOptions,
        async () => {
            await reInitSwiper()
        },
        { deep: true }
    )

    return {
        swiper,
        swiperActiveIndex,
        isSwiperInited,
        isButtonPrevDisabled,
        isButtonNextDisabled,
        reInitSwiper,
    }
}
